/**
 * Artome serial number parsing — the single source of truth for turning a
 * serial into a product.
 *
 * Two accepted formats:
 *   new: prefix + 3 digits + 2 alphanumerics + 5 digits   e.g. S02232XX00000
 *   old: prefix + 4-5 digits + "X"                        e.g. M1012345X
 */

export type ProductKey =
  | "S1"
  | "S2"
  | "S10PPS"
  | "X10"
  | "X12"
  | "M10"
  | "X20";

/**
 * Serial prefix to product. A lookup rather than a chain of comparisons, so a
 * prefix can never fall through and yield the raw (possibly lowercase) input —
 * which is what the previous implementation did.
 *
 * Several products accept more than one prefix, and the X1x pair is split across
 * two product lines: X1M is an X10, X1E is an X12. They differ only in their
 * final character, so take care when editing.
 */
const PREFIX_TO_PRODUCT: Record<string, ProductKey> = {
  S1: "S1",
  S01: "S1",
  S02: "S2",
  P01: "S10PPS",
  X1M: "X10",
  X10: "X10",
  X1E: "X12",
  X12: "X12",
  M10: "M10",
  X20: "X20",
};

/**
 * Longest first: regex alternation matches the first branch that fits, so a
 * short prefix listed earlier would shadow a longer one starting with the same
 * characters (an `S12` would be eaten by the `S1` branch). Nothing is shadowed
 * today, but sorting makes the pattern independent of key order above.
 */
const PREFIXES = Object.keys(PREFIX_TO_PRODUCT)
  .sort((a, b) => b.length - a.length)
  .join("|");

const NEW_FORMAT = new RegExp(`^(${PREFIXES})\\d{3}[A-Za-z0-9]{2}\\d{5}$`, "i");
const OLD_FORMAT = new RegExp(`^(${PREFIXES})\\d{4,5}X$`, "i");

export interface ParsedSerial {
  valid: boolean;
  /** Uppercased serial, safe to use as a Firestore key. */
  normalized: string;
  /** Matched prefix, uppercased. Absent when invalid. */
  prefix?: string;
  /** Friendly product name. Absent when invalid. */
  product?: ProductKey;
}

/** Normalize a serial for storage and querying. Always uppercase, trimmed. */
export const normalizeSerial = (input: string): string =>
  input.trim().toUpperCase();

export function parseArtomeSerial(input: string): ParsedSerial {
  const normalized = normalizeSerial(input);
  const match = NEW_FORMAT.exec(normalized) ?? OLD_FORMAT.exec(normalized);

  if (!match) {
    return { valid: false, normalized };
  }

  const prefix = match[1].toUpperCase();
  return {
    valid: true,
    normalized,
    prefix,
    product: PREFIX_TO_PRODUCT[prefix],
  };
}

/** Accepted length range for a projector serial. */
export const PROJECTOR_SERIAL_MIN = 9;
export const PROJECTOR_SERIAL_MAX = 14;

/**
 * Projector (Epson) serial validation. Deliberately loose: the authoritative
 * Epson format is not documented anywhere in this project, so this only rejects
 * obvious mistakes rather than guessing a pattern.
 *
 * The bounds are exported so the form's helper and error text cannot drift from
 * the rule they describe.
 */
export function isValidProjectorSerial(input: string): boolean {
  const value = input.trim();
  return new RegExp(
    `^[A-Za-z0-9]{${PROJECTOR_SERIAL_MIN},${PROJECTOR_SERIAL_MAX}}$`
  ).test(value);
}
